import React from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { SmsDeliveryMechanism } from '@cash-web/protos/squareup/franklin/common/sms_delivery_mechanism.pb'
import { PhoneNumberBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { hasOONIntent, optionalScopeHeaders, trySetOnboardingContext } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationRegisterSms } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterSms'
import { useRegion } from '@cash-web/shared-data-access-region-provider'
import { isSupportedRegion, PhoneInput, PhoneValue } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { formatPhoneNumberE164 } from '@cash-web/shared-util-formatting'

import { getRegisterSMSErrors } from '../../util/statusErrors'
import { BaseBlocker, SubmitButton } from '../'

export type BlockerPhoneNumberProps = {
  supplement?: PhoneNumberBlockerSupplement
}

export function BlockerPhoneNumber({ supplement }: BlockerPhoneNumberProps): React.ReactElement {
  const profileCountry = useAuth().profile?.country_code
  const regionCode = useRegion()
  const countryCode = profileCountry || regionCode

  const supportedCountryCode = countryCode && isSupportedRegion(countryCode) ? countryCode : 'US'
  const defaultValues = {
    regionCode: supportedCountryCode,
    phone: '',
  }

  const form = useForm<PhoneValue>({ defaultValues, mode: 'onTouched' })
  const { t } = useTranslation()
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const flow = useFlowContext()
  const { flowToken, clientScenario, paymentTokens } = flow.active ?? {}
  const { mutateAsync: registerSms } = useMutationRegisterSms({
    options: {
      clientScenario,
      flowToken: flowToken,
      optionalRequestHeaders: optionalScopeHeaders(),
    },
  })

  const onSubmit = async ({ phone, regionCode }: PhoneValue) => {
    const formattedNumber = formatPhoneNumberE164(phone, regionCode)

    await trySetOnboardingContext({ flowToken })
    await handleBlockerResponse({
      promisedBlockerUpdate: registerSms({
        sms_number: formattedNumber,
        existing_customer_only: !hasOONIntent() && clientScenario === ClientScenario.ONBOARDING,
        verification_mechanism: SmsDeliveryMechanism.SMS_TEXT,
        payment_tokens: paymentTokens,
      }),
      errors: getRegisterSMSErrors(genericError, t),
    })
  }

  return (
    <BaseBlocker
      mainText={supplement?.main_text ?? t('Confirm your phone number', { context: 'title' })}
      secondaryText={
        supplement?.input_hint_text ??
        t('Enter your mobile phone number to confirm your identity.', { context: 'subtitle' })
      }
      onSubmit={onSubmit}
      form={form}
      buttons={[<SubmitButton key="submit-phone-btn" />]}
    >
      <PhoneInput isLabelHidden={hasOONIntent()} autoFocus disableRegionCode={hasOONIntent()} />
    </BaseBlocker>
  )
}
